import * as React from "react";
import { cn } from "./cn.js";

export interface SwitchProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "onChange" | "type"> {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  /** The visible label. Required — an unlabelled switch is unusable by keyboard. */
  label: React.ReactNode;
  /** Secondary line under the label. */
  description?: React.ReactNode;
  /**
   * Words for the two states, announced after the label so the meaning never
   * rests on the knob position alone.
   */
  stateLabels?: { on: string; off: string };
}

/**
 * A two-state switch built on a real `<button role="switch">`.
 *
 * Native semantics on purpose: space and enter both toggle it, `aria-checked`
 * carries the state, and the state word is rendered as text as well as drawn,
 * so neither colour nor knob position is the only signal.
 */
export const Switch = React.forwardRef<HTMLButtonElement, SwitchProps>(function Switch(
  { className, checked, onCheckedChange, label, description, stateLabels, disabled, ...props },
  ref,
) {
  const words = stateLabels ?? { on: "On", off: "Off" };
  return (
    <button
      ref={ref}
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onCheckedChange(!checked)}
      className={cn(
        "group flex w-full items-start gap-3 rounded-md border p-3 text-left transition-colors",
        "disabled:cursor-not-allowed disabled:opacity-50",
        checked
          ? "border-[hsl(var(--warning))] bg-[hsl(var(--warning))]/10"
          : "border-input bg-background hover:bg-accent",
        className,
      )}
      {...props}
    >
      <span
        aria-hidden="true"
        className={cn(
          "mt-0.5 inline-flex h-5 w-9 shrink-0 items-center rounded-full border transition-colors",
          checked
            ? "border-[hsl(var(--warning))] bg-[hsl(var(--warning))]"
            : "border-input bg-muted",
        )}
      >
        <span
          className={cn(
            "size-4 rounded-full bg-background shadow-sm transition-transform",
            checked ? "translate-x-4" : "translate-x-0.5",
          )}
        />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-medium leading-tight">
          {label}{" "}
          <span
            className={cn(
              "ml-1 rounded px-1.5 py-0.5 text-[0.7rem] font-semibold uppercase tracking-wide",
              checked
                ? "bg-[hsl(var(--warning))] text-[hsl(var(--warning-foreground))]"
                : "bg-secondary text-secondary-foreground",
            )}
          >
            {checked ? words.on : words.off}
          </span>
        </span>
        {description ? (
          <span className="mt-1 block text-xs text-muted-foreground">{description}</span>
        ) : null}
      </span>
    </button>
  );
});
